import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { importClubFromWebsite } from "../../components/funtionsBD/Mainservice";

const PINGFLO = "#2A9BB5";

const MISSING_LABELS = {
  email: "mail",
  phone: "phone",
  address: "address",
  city: "city",
  contact: "contactPerson",
};

export default function ImportClubFromWebsiteDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setUrl("");
    setLoading(false);
    setError("");
    setResult(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  const handleDone = () => {
    const created = result;
    reset();
    onCreated?.(created);
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const website = url.trim();
    if (!website || loading) return;
    setLoading(true);
    setError("");
    try {
      const response = await importClubFromWebsite(website);
      setResult(response.data);
    } catch (err) {
      setError(err?.message || t("importClubFailed"));
    } finally {
      setLoading(false);
    }
  };

  const missing = (result?.missing || [])
    .map((key) => t(MISSING_LABELS[key] || key))
    .filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderTop: `4px solid ${PINGFLO}` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography
          component="p"
          sx={{
            m: 0,
            color: PINGFLO,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Pingflo
        </Typography>
        {t("importClubTitle")}
      </DialogTitle>
      <DialogContent>
        {result ? (
          <div>
            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
              {result.club_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("importClubCreated")}. {t("importClubInactive")}
            </Typography>
            <Typography variant="body2">
              {t("importClubRoom")}: {result.troom_name}
            </Typography>
            <Typography variant="body2">
              {result.tables_assumed
                ? t("importClubAssumedTables", { count: result.table_count })
                : t("importClubTables", { count: result.table_count })}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("importClubGroup")}: {result.gr_name}
            </Typography>
            {result.country_assumed ? (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {t("importClubCountryAssumed")}
              </Alert>
            ) : null}
            {missing.length ? (
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                {t("importClubMissing")}: {missing.join(", ")}
              </Typography>
            ) : null}
          </div>
        ) : (
          <form id="import-club-website" onSubmit={handleSubmit}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {t("importClubLead")}
            </Typography>
            <TextField
              autoFocus
              required
              fullWidth
              type="text"
              name="club-website"
              label={t("importClubUrl")}
              placeholder="https://"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              disabled={loading}
              inputProps={{
                style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" },
              }}
            />
            {loading ? (
              <Typography variant="body2" sx={{ mt: 2, color: PINGFLO }}>
                {t("importClubReading")}
              </Typography>
            ) : null}
            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : null}
          </form>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result ? (
          <Button variant="contained" onClick={handleDone} sx={{ bgcolor: PINGFLO, "&:hover": { bgcolor: "#1E7A8C" } }}>
            {t("importClubDone")}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              {t("importClubCancel")}
            </Button>
            <Button
              type="submit"
              form="import-club-website"
              variant="contained"
              disabled={loading || !url.trim()}
              sx={{ bgcolor: PINGFLO, "&:hover": { bgcolor: "#1E7A8C" } }}
            >
              {t("importClubSubmit")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
